import { FunctionComponent } from "react";

import {
  MessageBar,
  MessageBarType,
  Stack,
  SubmissionForm,
  TextInputField,
  useTheme
} from "@bps/fluent-ui";
import { UseFormMethods } from "@bps/form-api/dist/src/lib/form-api.types";
import { HttpError } from "@bps/http-client";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Selector } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useSelectorDataTypeRefData } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { SelectorFormValues } from "./formUtils";
import {
  selectorFormSchema,
  selectorFormValidator
} from "./SelectorFormValidator";
import { operatorOptions, SelectorRuleTable } from "./SelectorRuleTable";

interface SelectorFormProps {
  onSubmit: (
    values: SelectorFormValues,
    actions: Pick<UseFormMethods<SelectorFormValues>, "reset" | "setValue">
  ) => Promise<void>;
  onDismiss: () => void;
  selector?: Selector;
  error?: HttpError | null;
}

const SelectorFormBase: React.FC<SelectorFormProps> = ({
  onSubmit,
  selector,
  onDismiss,
  error
}) => {
  const theme = useTheme();

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const defaultValues: SelectorFormValues = {
    description: selector?.description ?? "",
    name: selector?.name ?? "",
    rules: selector?.rules.map(rule => {
      return {
        attributeType: rule.attributeType,
        comparisonOperator: rule.comparisonOperator,
        id: rule.id,
        value: rule.value,
        valueDataType: rule.valueDataType
      };
    }) ?? [
      {
        attributeType: "",
        value: "",
        comparisonOperator: operatorOptions[0].key, // default operator is "="
        id: crypto.randomUUID(),
        valueDataType: "STRING"
      }
    ]
  };

  return (
    <SubmissionForm<SelectorFormValues>
      onSubmit={onSubmit}
      initialValues={defaultValues}
      submitButtonProps={{ text: selector === undefined ? "Create" : "Update" }}
      cancelButtonProps={{ onClick: onDismiss, text: "Cancel" }}
      disabled={!hasWritePermission}
      validate={values =>
        selectorFormValidator.validate(values, selectorFormSchema)
      }
    >
      {() => {
        return (
          <>
            {error && (
              <MessageBar messageBarType={MessageBarType.error}>
                {error?.detail as string}
              </MessageBar>
            )}

            <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
              <TextInputField
                label="Selector Name"
                required
                name="name"
                styles={{ root: { width: 250 } }}
              />
              <TextInputField
                label="Description"
                name="description"
                styles={{ root: { width: 250 } }}
              />

              <SelectorRuleTable selectorMode={true} />
            </Stack>
          </>
        );
      }}
    </SubmissionForm>
  );
};

export const SelectorForm: FunctionComponent<SelectorFormProps> = props => {
  const dataTypesQuery = useSelectorDataTypeRefData();

  return (
    <QueryStateIndicator {...dataTypesQuery}>
      <SelectorFormBase {...props} />
    </QueryStateIndicator>
  );
};
